import { Feature, MapBrowserEvent, Map as OLMap } from 'ol';
import { FeatureLike } from 'ol/Feature';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import RenderFeature from 'ol/render/Feature';
import VectorTileSource from 'ol/source/VectorTile';
import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import React from 'react';

import { FeatureSelectCallback } from '../components/Map/lib/types';

import { useCurrentActor } from './useCurrentActor';

const DEFAULT_MAX_PRIORITY = 5;
const DEFAULT_MIN_PRIORITY = 1;
const SHADE_COUNT = 10;

const shapeStyles: { [key: string]: Style } = {};
const hoverShapeStyles: { [key: string]: Style } = {};

for (let i = 0; i <= SHADE_COUNT; i++) {
  /* eslint-disable @typescript-eslint/no-magic-numbers */
  shapeStyles[i] = new Style({
    fill: new Fill({
      color: `hsla(120, 64%, ${(65 - 5 * i).toFixed()}%, ${(0.2 + 0.06 * i).toFixed(2)})`,
    }),
  });
  hoverShapeStyles[i] = new Style({
    fill: new Fill({
      color: `hsla(120, 64%, ${(65 - 5 * i).toFixed()}%, ${(0.05 + 0.05 * i).toFixed(2)})`,
    }),
  });
  /* eslint-enable @typescript-eslint/no-magic-numbers */
}

export const styleIndex = (
  feature: FeatureLike,
  maxPriorityProp: number | undefined,
  minPriorityProp: number | undefined,
): number => {
  const maxPriority = maxPriorityProp || DEFAULT_MAX_PRIORITY;
  const minPriority = minPriorityProp || DEFAULT_MIN_PRIORITY;
  const { priority } = feature.getProperties();
  const range = maxPriority - minPriority;

  return Math.round(SHADE_COUNT * (Math.max(0, Math.min(priority, maxPriority) - minPriority) / range));
};

const usePostalTileLayer = (map: OLMap | undefined, handleSelect: FeatureSelectCallback) => {
  const { maxPriority, minPriority } = useCurrentActor();
  const layerRef = React.useRef<VectorTileLayer | undefined>(undefined);
  const sourceRef = React.useRef<VectorTileSource | undefined>(undefined);
  const hoveredPostalDigitsRef = React.useRef<number | undefined>(undefined);

  const styleParamsRef = React.useRef({ maxPriority, minPriority });

  React.useEffect(() => {
    if (!map) {
      return;
    }

    const source = new VectorTileSource({
      format: new MVT(),
      url: '/postal_codes/{z}/{x}/{y}',
      maxZoom: 14,
    });
    sourceRef.current = source;

    const layer = new VectorTileLayer({
      // Draw polygons straight to the layer canvas instead of caching one canvas per tile.
      // The per-tile canvases exhaust canvas memory on Firefox Android, causing stale and pixelated tiles.
      renderMode: 'vector',
      source,
      style: (feature: FeatureLike) => {
        const { maxPriority: maxP, minPriority: minP } = styleParamsRef.current;
        const postalDigits = feature.get('postal_digits');
        if (postalDigits === hoveredPostalDigitsRef.current) {
          return hoverShapeStyles[styleIndex(feature, maxP, minP)];
        }

        return shapeStyles[styleIndex(feature, maxP, minP)];
      },
    });
    layerRef.current = layer;

    const handlePointerMove = (e: MapBrowserEvent) => {
      const tileFeature = e.map
        .getFeaturesAtPixel(e.pixel)
        .find((f) => f instanceof RenderFeature && f.get('postal_digits'));

      const postalDigits = tileFeature?.get('postal_digits');
      if (postalDigits !== hoveredPostalDigitsRef.current) {
        hoveredPostalDigitsRef.current = postalDigits;
        layer.changed();
      }
    };

    map.getLayers().insertAt(1, layer);
    map.on('pointermove', handlePointerMove);

    const handleTileClick = (e: MapBrowserEvent) => {
      if (handleSelect) {
        const features: Feature[] = [];
        const postalTiles: RenderFeature[] = [];

        e.map.getFeaturesAtPixel(e.pixel).forEach((f) => {
          if (f instanceof Feature) {
            features.push(f);
          } else if (f instanceof RenderFeature) {
            if (f.get('postal_digits') || f.get('postcode5')) {
              postalTiles.push(f);
            }
          }
        });

        if (features.length === 0 && postalTiles.length > 0) {
          handleSelect(postalTiles[0], e.coordinate);
        }
      }
    };

    map.on('click', handleTileClick);

    return () => {
      map.removeLayer(layer);
      map.un('pointermove', handlePointerMove);
      map.un('click', handleTileClick);
      layerRef.current = undefined;
      sourceRef.current = undefined;
    };
  }, [map]);

  React.useEffect(() => {
    styleParamsRef.current = { maxPriority, minPriority };
    // Trigger tile layer re-render when selection or priorities change
    layerRef.current?.changed();
  }, [maxPriority, minPriority]);
};

export default usePostalTileLayer;
